import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';
import { deepPurple, orange, green, blueGrey } from '@mui/material/colors';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h6Bold: true;
  }
}

declare module '@mui/material/styles' {
  interface Shape {
    borderRadius: number;
  }

  interface Theme {
    shape: Shape;
  }

  interface ShapeOptions {
    borderRadius?: number;
  }

  interface ThemeOptions {
    shape?: ShapeOptions;
  }

  interface TypographyVariants {
    h6Bold: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    h6Bold?: React.CSSProperties;
  }

  interface SimplePaletteColor {
    states?: {
      hover: string;
      selected: string;
      focus: string;
      focusVisible: string;
      outlinedBorder: string;
    };
  }

  interface PaletteColor extends SimplePaletteColor {
    light: string;
    dark: string;
    contrastText: string;
  }

  interface Palette {
    disabled: {
      main: string;
      light: string;
    };
    primaryAlternative: {
      lighter: string;
      darker: string;
      darkest: string;
      special: string;
    };
    paper: {
      background: string;
      light: string;
      dark: string;
    };
    alphas: {
      halfWhite: string;
      divider: string;
      cascote: string;
      piedrazo: string;
    };
    field: {
      main: string;
    };
    chips: {
      recommended: {
        background: string;
        text: string;
      };
    };
    yellow: {
      100: string;
    };
    cases: {
      open: {
        bg: string;
        text: string;
      };
      inProgress: {
        bg: string;
        text: string;
      };
      needsClientAction: {
        bg: string;
        text: string;
      };
      needsAgentAction: {
        bg: string;
        text: string;
      };
      done: {
        bg: string;
        text: string;
      };
      canceled: {
        bg: string;
        text: string;
      };
    };
    inherit: {
      white: SimplePaletteColor;
      textPrimary: SimplePaletteColor;
    };
  }

  interface PaletteOptions {
    inherit?: {
      white: SimplePaletteColor;
      textPrimary: SimplePaletteColor;
    };
  }

  interface Components {
    MuiPickersCalendarHeader?: {
      styleOverrides?: {
        switchViewButton: {
          display: string;
        };
        labelContainer: {
          pointerEvents: string;
        };
      };
    };
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            border: string;
            outline: string;
            backgroundColor: string;
            color: string;
          };
          '&:hover': {
            border: 'none';
            outline: 'none !important';
            backgroundColor: string;
            color: string;
          };
        };
      };
    };
  }
}

const action = {
  hoverOpacity: 0.04,
  selectedOpacity: 0.08,
  disabledOpacity: 0.38,
  focusOpacity: 0.12,
  focusVisibleOpacity: 0.3,
  outlinedBorderOpacity: 0.5,
  activeChannel: 'rgb(0, 0, 0)',
  selectedChannel: 'rgb(0, 0, 0)',
};

const getStateColors = (color: string) => ({
  hover: alpha(color, action.hoverOpacity),
  selected: alpha(color, action.selectedOpacity),
  focus: alpha(color, action.focusOpacity),
  focusVisible: alpha(color, action.focusVisibleOpacity),
  outlinedBorder: alpha(color, action.outlinedBorderOpacity),
});

const palette = {
  yellow: {
    100: '#FFF9C4',
  },
  cases: {
    open: {
      bg: '#0288D1',
      text: 'white',
    },
    inProgress: {
      bg: '#ED6C02',
      text: 'white',
    },
    needsClientAction: {
      bg: '#FFB300',
      text: 'black',
    },
    needsAgentAction: {
      bg: '#EF5350',
      text: 'white',
    },
    done: {
      bg: '#4CAF50',
      text: 'white',
    },
    canceled: {
      bg: alpha('#000', 0.2),
      text: '#000',
    },
  },
  primary: {
    main: blueGrey[700],
    light: blueGrey[400],
    dark: blueGrey[800],
    contrastText: '#fff',
    states: getStateColors(blueGrey[900]),
  },
  secondary: {
    main: '#FFB300',
    light: '#FFD268',
    dark: '#A97700',
    contrastText: 'rgba(0,0,0,0.87)',
    states: getStateColors('#FFB300'),
  },
  text: {
    primary: 'rgba(0,0,0,0.87)',
    secondary: 'rgba(0,0,0,0.6)',
    disabled: 'rgba(0,0,0,0.38)',
  },
  success: {
    main: '#4CAF50',
    light: green[500],
    dark: green[900],
    contrastText: '#FFFFFF',
    states: getStateColors('#4CAF50'),
  },
  disabled: {
    main: 'rgba(0, 0, 0, 0.38)',
    light: 'rgb(189, 189, 189, 1)',
  },
  warning: {
    main: '#ED6C02',
    light: orange[500],
    dark: orange[900],
    contrastText: '#fff',
    states: getStateColors('#ED6C02'),
  },
  field: {
    main: '#FFF',
  },
  //From here those extended from the Palette type above
  primaryAlternative: {
    lighter: deepPurple[50],
    darker: deepPurple[100],
    darkest: deepPurple[500],
    special: deepPurple.A100,
  },
  paper: {
    background: '#fafafa',
    light: '#fff',
    dark: '#f5f5f5',
  },
  alphas: {
    halfWhite: 'rgba(255,255,255,0.5)',
    divider: 'rgba(0, 0, 0, 0.12)',
    cascote: 'rgba(74, 20, 140, 0.08)',
    piedrazo: 'rgba(74, 20, 140, 0.04)',
  },
  chips: {
    recommended: { background: green.A200, text: '#0000008F' },
  },
  action: {
    hover: alpha(action.activeChannel, action.hoverOpacity),
    selected: alpha(action.selectedChannel, action.selectedOpacity),
    disabled: alpha(action.activeChannel, action.disabledOpacity),
    disabledBackground: alpha(action.activeChannel, 0.12),
    focus: alpha(action.activeChannel, action.focusOpacity),
    active: alpha(action.activeChannel, 0.56),
    hoverOpacity: action.hoverOpacity,
    selectedOpacity: action.selectedOpacity,
    disabledOpacity: action.disabledOpacity,
    focusOpacity: action.focusOpacity,
  },
  inherit: {
    white: {
      main: '#FFFFFF',
      states: getStateColors('#FFFFFF'),
    },
    textPrimary: {
      main: 'rgba(0,0,0,0.87)',
      states: getStateColors('rgba(0,0,0,0.87)'),
    },
  },
};

const typography: ThemeOptions['typography'] = {
  fontFamily: 'Montserrat, sans-serif',
  subtitle1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '28px',
  },
  subtitle2: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '22px',
  },
  body1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  body2: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.17px',
  },
  h5: {
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '32px',
  },
  h6: {
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '32px',
    textAlign: 'left',
    letterSpacing: '0.15px',
  },
  h6Bold: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '32px',
    textAlign: 'left',
    letterSpacing: '0.15px',
  },
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.4px',
  },
};

export const theme = createTheme({
  shape: {
    borderRadius: 8,
  },
  typography,
  palette,
  components: {
    MuiGrid: {
      styleOverrides: {
        root: {
          margin: '0 !important', // Turbio as fuck i know, pero lo estuvimos viendo con marco, tira de la nada un margin 48 que nos invalida de usar el grid si no
          padding: '8px',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& .MuiFormLabel-root.MuiInputLabel-root': {
            top: '3px',
            '&.Mui-focused': {
              top: '-3px !important',
            },
          },
          '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink': {
            top: '-3px !important',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          letterSpacing: '0.17px',
          '&&:not(.MuiInputBase-multiline )': {
            minHeight: '40px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: palette.field.main,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '8px',
          '&.Mui-error': {
            '&>.MuiOutlinedInput-notchedOutline': {
              border: '1px solid #d32f2f',
            },
          },
        },
        notchedOutline: {
          border: 0,
          borderRadius: '8px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&&': {
            top: -3,
            fontSize: '14px',
            lineHeight: 1,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        labelIcon: {
          gap: 4,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
          },
          '&:blur': {
            outline: 'none',
          },
          '&.MuiPickersArrowSwitcher-button': {
            border: 'none',
            outline: 'none !important',
            '&:hover': {
              backgroundColor: palette.primary.light,
              color: palette.primary.contrastText,
            },
            '&:active': {
              backgroundColor: palette.primary.dark,
              color: palette.primary.contrastText,
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          '&:focus': {
            outline: 'none',
          },
          '&:blur': {
            outline: 'none',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '&& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root': {
            backgroundColor: palette.paper.dark,
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          display: 'none',
        },
        labelContainer: {
          pointerEvents: 'none',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            border: 'none',
            outline: 'none !important',
            backgroundColor: palette.primary.dark,
            color: palette.primary.contrastText,
          },
          '&:hover': {
            border: 'none',
            outline: 'none !important',
            backgroundColor: palette.primary.light,
            color: palette.primary.contrastText,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '& .MuiRadio-root + .MuiFormControlLabel-label': {
            textTransform: 'lowercase',
            ...typography.body2,
          },
        },
      },
    },
  },
});

export {};
